import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import './CreateCampaign.css';

const FIELD_TYPES = ['text','integer','float','categorical','boolean','date'];
const CATEGORIES = ['demographics','surgery','history','preoperative','complications','follow_up_3mo','follow_up_6mo','follow_up_12mo','follow_up_24mo','fusion','outcomes'];
const STUDY_TYPES = ['retrospective','prospective','randomized','registry'];

const PHI_PATTERNS = [
  /first\s*name/i, /last\s*name/i, /birth\s*name/i, /used\s*name/i, /maiden/i, /surname/i,
  /\bnom\b/i, /\bprenom\b/i, /\bprénom\b/i,
  /\bemail\b/i, /\bmail\b/i, /\bcourriel\b/i,
  /\bphone\b/i, /\btelephone\b/i, /\btéléphone\b/i, /\btel\b$/i, /\bmobile\b/i, /\bportable\b/i,
  /\baddress\b/i, /\badresse\b/i, /\bzip\b/i, /\bpostal\b/i, /\bville\b/i, /\brue\b/i,
  /birth\s*date/i, /date.*naissance/i, /\bdob\b/i,
  /\bssn\b/i, /\bnir\b/i, /social.*security/i,
  /\bserial\b/i, /medical.*record/i, /\bmrn\b/i, /\bdossier\b/i,
  /\bchirurgien\b/i, /surgeon.*name/i, /\boperator\b/i, /\bmedecin\b/i,
  /\blabel\b/i,
];

const CATEGORY_HINTS = {
  demo: 'demographics', age: 'demographics', sex: 'demographics', genre: 'demographics', taille: 'demographics', poids: 'demographics', imc: 'demographics', bmi: 'demographics', tabac: 'demographics', fum: 'demographics', profession: 'demographics', emploi: 'demographics', sport: 'demographics', alcool: 'demographics',
  preop: 'preoperative', pré: 'preoperative', odi: 'preoperative', eva: 'preoperative', vas: 'preoperative', diagno: 'preoperative', symptom: 'preoperative', imagerie: 'preoperative', irm: 'preoperative', scanner: 'preoperative', sténose: 'preoperative',
  chir: 'surgery', surg: 'surgery', operat: 'surgery', abord: 'surgery', niveau: 'surgery', asa: 'surgery', durée: 'surgery', implant: 'surgery', approach: 'surgery', cote: 'surgery',
  complic: 'complications', reprise: 'complications', reoper: 'complications',
  m1: 'follow_up_3mo', '3m': 'follow_up_3mo', m3: 'follow_up_3mo',
  m6: 'follow_up_6mo', '6m': 'follow_up_6mo',
  m12: 'follow_up_12mo', '12m': 'follow_up_12mo', '1an': 'follow_up_12mo',
  m24: 'follow_up_24mo', '24m': 'follow_up_24mo', '2an': 'follow_up_24mo',
  fusion: 'fusion', radio: 'fusion', consoli: 'fusion',
  satisf: 'outcomes', amélior: 'outcomes', improv: 'outcomes', retour: 'outcomes', return: 'outcomes', travail: 'outcomes', work: 'outcomes',
  hospit: 'surgery', séjour: 'surgery', entrée: 'surgery', sortie: 'surgery',
  antecedent: 'history', atcd: 'history', prior: 'history', ancien: 'history',
};

function isPHI(name) { return PHI_PATTERNS.some(p => p.test(name)); }

function guessCategory(name) {
  const lower = name.toLowerCase();
  for (const [hint, cat] of Object.entries(CATEGORY_HINTS)) {
    if (lower.includes(hint)) return cat;
  }
  return 'demographics';
}

function inferFieldType(values) {
  const sample = values.filter(v => v !== '' && v !== null && v !== undefined).slice(0, 50);
  if (!sample.length) return 'text';
  const nums = sample.filter(v => !isNaN(parseFloat(v)));
  if (nums.length > sample.length * 0.8) return sample.some(v => String(v).includes('.')) ? 'float' : 'integer';
  const dates = sample.filter(v => /^\d{4}-\d{2}-\d{2}|^\d{1,2}[/\-]\d{1,2}[/\-]\d{4}/.test(String(v)));
  if (dates.length > sample.length * 0.6) return 'date';
  const bools = sample.filter(v => ['yes','no','true','false','oui','non','0','1'].includes(String(v).toLowerCase()));
  if (bools.length > sample.length * 0.8) return 'boolean';
  const unique = new Set(sample.map(v => String(v).toLowerCase()));
  if (unique.size <= 15 && sample.length > 5) return 'categorical';
  return 'text';
}

function slugify(name) {
  return name.toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 60);
}

const TEMPLATES = {
  blank: { name: 'Blank Campaign', arms: [], fields: [] },
  spine_basic: {
    name: 'Spine Surgery Basic',
    arms: [{ name: 'Treatment', slug: 'treatment' }],
    fields: [
      { field_name: 'sex', field_label: 'Sex', data_type: 'categorical', category: 'demographics', is_required: true, options: ['Male','Female','Other'] },
      { field_name: 'age_at_surgery', field_label: 'Age at Surgery', data_type: 'integer', category: 'demographics', is_required: true, validation_rules: { min: 18, max: 100 } },
      { field_name: 'height_m', field_label: 'Height (m)', data_type: 'float', category: 'demographics' },
      { field_name: 'weight_kg', field_label: 'Weight (kg)', data_type: 'float', category: 'demographics' },
      { field_name: 'bmi', field_label: 'BMI', data_type: 'float', category: 'demographics', validation_rules: { min: 15, max: 60 } },
      { field_name: 'smoking_status', field_label: 'Smoking Status', data_type: 'categorical', category: 'demographics', options: ['Never','Former','Current (<10/day)','Current (>10/day)'] },
      { field_name: 'surgery_date', field_label: 'Surgery Date', data_type: 'date', category: 'surgery', is_required: true },
      { field_name: 'procedure_name', field_label: 'Procedure', data_type: 'text', category: 'surgery', is_required: true },
      { field_name: 'approach', field_label: 'Approach', data_type: 'categorical', category: 'surgery', options: ['Open','MIS','Endoscopic','Percutaneous'] },
      { field_name: 'operative_time_min', field_label: 'Operative Time (min)', data_type: 'integer', category: 'surgery' },
      { field_name: 'preop_odi', field_label: 'Pre-op ODI', data_type: 'float', category: 'preoperative', validation_rules: { min: 0, max: 100 } },
      { field_name: 'preop_vas_back', field_label: 'Pre-op VAS Back', data_type: 'integer', category: 'preoperative', validation_rules: { min: 0, max: 10 } },
      { field_name: 'preop_vas_leg', field_label: 'Pre-op VAS Leg', data_type: 'integer', category: 'preoperative', validation_rules: { min: 0, max: 10 } },
    ]
  }
};

function CreateCampaign({ onClose, onCreated, editData }) {
  const isEdit = !!editData;
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [campaign, setCampaign] = useState({
    name: '', slug: '', description: '', study_type: 'retrospective', pi_name: '', primary_outcome: ''
  });
  const [arms, setArms] = useState([{ name: '', slug: '' }]);
  const [fields, setFields] = useState([]);
  const [newField, setNewField] = useState({ field_name: '', field_label: '', data_type: 'text', category: 'demographics', is_required: false, options: '', description: '' });
  const [hasPatients, setHasPatients] = useState(false);
  const [importStats, setImportStats] = useState(null);

  useEffect(() => {
    if (editData) {
      setCampaign({
        name: editData.name || '', slug: editData.slug || '', description: editData.description || '',
        study_type: editData.study_type || 'retrospective', pi_name: editData.pi_name || '', primary_outcome: editData.primary_outcome || ''
      });
      if (editData.arms && editData.arms.length) setArms(editData.arms.map(a => ({ name: a.name, slug: a.slug })));
      if (editData.fields && editData.fields.length) {
        setFields(editData.fields.map(f => ({
          field_name: f.field_name, field_label: f.field_label || f.field_name, data_type: f.data_type || 'text',
          category: f.category || 'demographics', is_required: f.is_required || false,
          options: f.options || undefined, validation_rules: f.validation_rules || undefined, description: f.description || ''
        })));
      }
      setHasPatients(editData.patient_count > 0);
    }
  }, [editData]);

  const autoSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 50);

  const applyTemplate = (key) => {
    const t = TEMPLATES[key];
    if (t.fields.length) setFields(t.fields);
    if (t.arms.length) setArms(t.arms.map(a => ({ ...a })));
  };

  const handleFileImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
        if (!rows.length) return setError('File is empty');

        const cols = Object.keys(rows[0]);
        let phiCount = 0;
        const newFields = [];

        for (const col of cols) {
          if (isPHI(col)) { phiCount++; continue; }
          const values = rows.map(r => r[col]);
          const dtype = inferFieldType(values);
          const fname = slugify(col);
          if (!fname) continue;

          const field = {
            field_name: fname,
            field_label: col,
            data_type: dtype,
            category: guessCategory(col),
            is_required: false,
          };

          // Extract categories for categorical fields
          if (dtype === 'categorical') {
            const unique = [...new Set(values.filter(v => v !== '' && v != null).map(v => String(v)))];
            if (unique.length <= 20) field.options = unique.slice(0, 20);
          }

          newFields.push(field);
        }

        setFields(newFields);
        setImportStats({ total: cols.length, imported: newFields.length, phi: phiCount });
        setError('');
      } catch (err) { setError('Failed to read file: ' + err.message); }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const addField = () => {
    if (!newField.field_name) return;
    const f = { ...newField };
    if (f.options) f.options = f.options.split(',').map(o => o.trim()).filter(Boolean);
    else delete f.options;
    if (!f.description) delete f.description;
    setFields([...fields, f]);
    setNewField({ field_name: '', field_label: '', data_type: 'text', category: 'demographics', is_required: false, options: '', description: '' });
  };

  const removeField = (i) => setFields(fields.filter((_, idx) => idx !== i));
  const addArm = () => setArms([...arms, { name: '', slug: '' }]);
  const removeArm = (i) => setArms(arms.filter((_, idx) => idx !== i));
  const updateArm = (i, key, val) => { const a = [...arms]; a[i] = { ...a[i], [key]: val }; if (key === 'name') a[i].slug = autoSlug(val); setArms(a); };

  const handleSave = async () => {
    setError('');
    if (!campaign.name || !campaign.slug) return setError('Campaign name is required');
    if (!arms.filter(a => a.name).length) return setError('At least one study arm required');
    if (!fields.length) return setError('At least one field required');
    setSaving(true);
    try {
      const payload = { ...campaign, arms: arms.filter(a => a.name), fields };
      const res = isEdit
        ? await axios.put('/api/v2/campaigns/' + editData.slug, payload)
        : await axios.post('/api/v2/campaigns', payload);
      onCreated(res.data);
    } catch (err) { setError(err.response?.data?.error || err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? 'Edit Campaign' : 'New Research Campaign'}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="wizard-steps">
          {['Details', 'Study Arms', 'Data Fields', 'Review'].map((s, i) => (
            <div key={i} className={'wizard-step ' + (step === i + 1 ? 'active' : step > i + 1 ? 'done' : '')} onClick={() => setStep(i + 1)}>
              <span className="step-num">{step > i + 1 ? '\u2713' : i + 1}</span>
              <span className="step-label">{s}</span>
            </div>
          ))}
        </div>
        <div className="modal-body">
          {step === 1 && (
            <div className="step-content">
              <div className="form-row"><div className="form-group full">
                <label>Campaign Name *</label>
                <input type="text" value={campaign.name} placeholder="e.g., Open vs MIS vs Endoscopic TLIF"
                  onChange={e => setCampaign({ ...campaign, name: e.target.value, slug: isEdit ? campaign.slug : autoSlug(e.target.value) })} />
              </div></div>
              {isEdit && <div className="form-row"><div className="form-group full">
                <label>Slug (read-only)</label>
                <input type="text" value={campaign.slug} disabled className="input-disabled" />
              </div></div>}
              <div className="form-row"><div className="form-group">
                <label>Study Type</label>
                <select value={campaign.study_type} onChange={e => setCampaign({ ...campaign, study_type: e.target.value })}>
                  {STUDY_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div></div>
              <div className="form-row">
                <div className="form-group"><label>Principal Investigator</label>
                  <input type="text" value={campaign.pi_name} placeholder="Dr. Name" onChange={e => setCampaign({ ...campaign, pi_name: e.target.value })} /></div>
                <div className="form-group"><label>Primary Outcome</label>
                  <input type="text" value={campaign.primary_outcome} placeholder="e.g., Fusion rate at 24 months" onChange={e => setCampaign({ ...campaign, primary_outcome: e.target.value })} /></div>
              </div>
              <div className="form-row"><div className="form-group full"><label>Description</label>
                <textarea value={campaign.description} rows={3} placeholder="Brief description of the study..." onChange={e => setCampaign({ ...campaign, description: e.target.value })} />
              </div></div>
            </div>
          )}
          {step === 2 && (
            <div className="step-content">
              <p className="step-hint">Study arms group patients by treatment or condition.</p>
              {arms.map((arm, i) => (
                <div key={i} className="arm-row">
                  <input type="text" value={arm.name} placeholder="Arm name (e.g., Open TLIF)" onChange={e => updateArm(i, 'name', e.target.value)} />
                  <span className="arm-slug">{arm.slug || '\u2014'}</span>
                  {arms.length > 1 && <button className="btn-remove" onClick={() => removeArm(i)}>&times;</button>}
                </div>
              ))}
              <button className="btn-add" onClick={addArm}>+ Add Arm</button>
            </div>
          )}
          {step === 3 && (
            <div className="step-content">
              {!isEdit && (
                <div className="template-bar">
                  <span>Quick start:</span>
                  <button className="btn-template" onClick={() => applyTemplate('spine_basic')}>Spine Surgery Template</button>
                  <button className="btn-template" onClick={() => applyTemplate('blank')}>Clear All</button>
                  <span className="template-divider">|</span>
                  <label className="btn-template btn-import-file">
                    📄 Import from File
                    <input type="file" accept=".csv,.xlsx,.xls,.tsv" style={{display:'none'}} onChange={handleFileImport} />
                  </label>
                </div>
              )}
              {isEdit && hasPatients && <div className="edit-warning">This campaign has patients. You can add new fields and rename existing ones, but cannot remove fields that already have data.</div>}

              {importStats && (
                <div className="import-stats">
                  <span className="is-icon">✅</span>
                  <span>{importStats.imported} fields imported from {importStats.total} columns</span>
                  {importStats.phi > 0 && <span className="is-phi">🛡️ {importStats.phi} PHI columns excluded</span>}
                  <button className="is-close" onClick={() => setImportStats(null)}>&times;</button>
                </div>
              )}

              <div className="fields-list">
                {fields.map((f, i) => (
                  <div key={i} className="field-row">
                    <span className="fr-name">{f.field_label || f.field_name}</span>
                    <span className="fr-type">{f.data_type}</span>
                    <span className="fr-cat">{f.category}</span>
                    {f.is_required && <span className="fr-req">req</span>}
                    {(!isEdit || !hasPatients) && <button className="btn-remove" onClick={() => removeField(i)}>&times;</button>}
                  </div>
                ))}
              </div>
              <div className="add-field-form">
                <div className="aff-row">
                  <input type="text" placeholder="field_name" value={newField.field_name}
                    onChange={e => setNewField({ ...newField, field_name: e.target.value, field_label: e.target.value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) })} />
                  <select value={newField.data_type} onChange={e => setNewField({ ...newField, data_type: e.target.value })}>
                    {FIELD_TYPES.map(t => <option key={t}>{t}</option>)}</select>
                  <select value={newField.category} onChange={e => setNewField({ ...newField, category: e.target.value })}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}</select>
                </div>
                <div className="aff-row">
                  <input type="text" placeholder="Options (comma-separated)" value={newField.options} onChange={e => setNewField({ ...newField, options: e.target.value })} />
                  <label className="checkbox-label"><input type="checkbox" checked={newField.is_required} onChange={e => setNewField({ ...newField, is_required: e.target.checked })} /> Required</label>
                  <button className="btn-add" onClick={addField}>+ Add</button>
                </div>
              </div>
            </div>
          )}
          {step === 4 && (
            <div className="step-content review">
              <h3>{campaign.name || 'Untitled'}</h3>
              <p className="review-desc">{campaign.description || 'No description'}</p>
              <div className="review-meta">
                <span>{campaign.study_type}</span>
                {campaign.pi_name && <span>PI: {campaign.pi_name}</span>}
                {campaign.primary_outcome && <span>Primary: {campaign.primary_outcome}</span>}
              </div>
              <div className="review-section"><h4>Study Arms ({arms.filter(a => a.name).length})</h4>
                {arms.filter(a => a.name).map((a, i) => <div key={i} className="review-item">{a.name}</div>)}</div>
              <div className="review-section"><h4>Data Fields ({fields.length})</h4>
                <div className="review-fields">{fields.map((f, i) => (
                  <div key={i} className="review-field"><span>{f.field_label || f.field_name}</span><span className="rf-type">{f.data_type}</span></div>
                ))}</div></div>
            </div>
          )}
        </div>
        {error && <div className="modal-error">{error}</div>}
        <div className="modal-footer">
          {step > 1 && <button className="btn-back" onClick={() => setStep(step - 1)}>Back</button>}
          <div className="footer-spacer"></div>
          {step < 4 && <button className="btn-next" onClick={() => setStep(step + 1)}>Next</button>}
          {step === 4 && <button className="btn-save" onClick={handleSave} disabled={saving}>
            {saving ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save Changes' : 'Create Campaign')}
          </button>}
        </div>
      </div>
    </div>
  );
}
export default CreateCampaign;
